import { createStore, createEvent, createEffect, sample } from "effector";
import { User } from "../../../core/models/user";
import { AuthService, UserSettings } from "../service/authService";
import { fetchAllTermsRequested } from "@/core/store/terms";
import { fetchNotificationsRequested } from "../../notifications/store";
// ❌ REMOVE THIS IMPORT
// import { MercureService } from "../../../core/services/mercureService";
import {
  fetchConversationHistoryRequested,
  fetchConversationsRequested,
} from "../../chat/store/chatStore";

// Events
export const loginRequested = createEvent<{
  username: string;
  password: string;
}>();
export const googleLoginRequested = createEvent<{
  access_token: string;
  expires_in: number;
}>();
export const facebookLoginRequested = createEvent<{
  access_token: string;
  expires_in: number;
}>();
export const linkedinCodeReceived = createEvent<{ code: string }>();
export const logoutRequested = createEvent();
export const refreshTokenRequested = createEvent();
export const userProfileRequested = createEvent();
export const initializeAuth = createEvent();
export const redirectToReturnUrl = createEvent<string>();

// Effects
export const loginFx = createEffect(
  async ({ username, password }: { username: string; password: string }) => {
    try {
      if (!username || !password) {
        throw new Error("Username and password are required");
      }

      const response = await AuthService.login(username, password);
      // console.log("AuthService.login response:", response);

      if (!response.access_token) {
        throw new Error("Invalid response from login service");
      }

      try {
        localStorage.setItem("authToken", response.access_token);
        localStorage.setItem("refreshToken", response.refresh_token);
        // console.log("Tokens stored successfully");
      } catch (storageError) {
        console.warn("Failed to store auth tokens:", storageError);
      }

      const user = await AuthService.getUserProfile();
      // console.log("User profile loaded:", user);

      const userSettings = await AuthService.getUserSettings();
      // console.log("User settings loaded:", userSettings);

      const userWithUid = {
        ...user,
        uid: userSettings.uid,
      };

      try {
        localStorage.setItem("userProfile", JSON.stringify(userWithUid));
        localStorage.setItem("userSettings", JSON.stringify(userSettings));
        // console.log("User profile and settings stored successfully");
      } catch (storageError) {
        console.warn("Failed to store user data:", storageError);
      }

      return { ...response, user: userWithUid };
    } catch (error: any) {
      console.error("loginFx error:", error);
      throw error;
    }
  },
);

export const googleLoginFx = createEffect(
  async (obj: { access_token: string; expires_in: number }) => {
    try {
      const userData = await AuthService.getGoogleUser(obj.access_token);
      const authInfo = await AuthService.googleLogin(obj, userData);

      localStorage.setItem("authToken", authInfo.access_token);
      localStorage.setItem("refreshToken", authInfo.refresh_token);

      const user = await AuthService.getUserProfile();
      const userSettings = await AuthService.getUserSettings();
      const userWithUid = {
        ...user,
        uid: userSettings.uid,
      };

      localStorage.setItem("userProfile", JSON.stringify(userWithUid));
      localStorage.setItem("userSettings", JSON.stringify(userSettings));

      return { ...authInfo, user: userWithUid };
    } catch (error: any) {
      console.error("googleLoginFx error:", error);
      throw new Error(error.message || "Google login failed");
    }
  },
);

export const facebookLoginFx = createEffect(
  async (obj: { access_token: string; expires_in: number }) => {
    try {
      // console.log("facebookLoginFx called with:", obj);
      const userData = await AuthService.getFacebookUser(obj.access_token);
      // console.log("Facebook user data:", userData);

      const authInfo = await AuthService.facebookLogin(obj, userData);
      // console.log("Facebook auth response:", authInfo);

      localStorage.setItem("authToken", authInfo.access_token);
      localStorage.setItem("refreshToken", authInfo.refresh_token);

      const user = await AuthService.getUserProfile();
      const userSettings = await AuthService.getUserSettings();
      const userWithUid = {
        ...user,
        uid: userSettings.uid,
      };

      localStorage.setItem("userProfile", JSON.stringify(userWithUid));
      localStorage.setItem("userSettings", JSON.stringify(userSettings));

      return { ...authInfo, user: userWithUid };
    } catch (error: any) {
      console.error("facebookLoginFx error:", error);
      throw new Error(error.message || "Facebook login failed");
    }
  },
);

export const linkedinLoginFx = createEffect(async (obj: { code: string }) => {
  try {
    // console.log("linkedinLoginFx called with code:", obj.code);

    const authInfo = await AuthService.linkedinLogin(obj.code);
    // console.log("LinkedIn auth response:", authInfo);

    localStorage.setItem("authToken", authInfo.access_token);
    localStorage.setItem("refreshToken", authInfo.refresh_token);

    const user = await AuthService.getUserProfile();
    const userSettings = await AuthService.getUserSettings();
    const userWithUid = {
      ...user,
      uid: userSettings.uid,
    };

    localStorage.setItem("userProfile", JSON.stringify(userWithUid));
    localStorage.setItem("userSettings", JSON.stringify(userSettings));

    return { ...authInfo, user: userWithUid };
  } catch (error: any) {
    console.error("linkedinLoginFx error:", error);
    throw new Error(error.message || "LinkedIn login failed");
  }
});

export const logoutFx = createEffect(async () => {
  // console.log("logoutFx called");

  try {
    // ❌ REMOVE THESE LINES
    // MercureService.clearHandlers();
    // MercureService.disconnect();

    // ✅ Mercure cleanup is now handled automatically by MercureProvider unmounting

    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("userProfile");
    localStorage.removeItem("userSettings");
    // console.log("Tokens, user profile, and settings removed successfully");
  } catch (storageError) {
    console.warn("Failed to remove auth data:", storageError);
  }
});

export const initializeAuthFx = createEffect(async () => {
  // console.log("initializeAuthFx called");
  try {
    const token = localStorage.getItem("authToken");
    // console.log("Found token:", !!token);

    if (!token) {
      // console.log("No token found, user not authenticated");
      return { isAuthenticated: false, user: null };
    }

    let user = null;
    try {
      const storedUser = localStorage.getItem("userProfile");
      if (storedUser) {
        user = JSON.parse(storedUser);
        // console.log("User restored from localStorage:", user);
        return { isAuthenticated: true, user };
      }
    } catch (storageError) {
      console.warn("Failed to restore user from localStorage:", storageError);
    }

    // console.log("Attempting to validate token by fetching user profile...");
    user = await Promise.race([
      AuthService.getUserProfile(),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Profile fetch timeout")), 10000),
      ),
    ]);
    // console.log("Profile loaded successfully:", user);

    try {
      localStorage.setItem("userProfile", JSON.stringify(user));
      // console.log("User profile stored successfully");
    } catch (storageError) {
      console.warn("Failed to store user profile:", storageError);
    }

    return { isAuthenticated: true, user };
  } catch (error) {
    console.warn("Auth initialization failed:", error);

    try {
      localStorage.removeItem("authToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("userProfile");
      localStorage.removeItem("userSettings");
      // console.log("Cleared invalid auth data");
    } catch (storageError) {
      console.warn("Failed to remove invalid auth data:", storageError);
    }

    return { isAuthenticated: false, user: null };
  }
});

// ❌ REMOVE THIS ENTIRE EFFECT
// export const initializeMercureFx = createEffect(async (user: User) => { ... });

// Store
interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  initialized: boolean;
}

const getInitialAuthState = (): AuthState => {
  try {
    const hasToken = Boolean(localStorage.getItem("authToken"));
    // console.log("Initial auth state - hasToken:", hasToken);
    return {
      user: null,
      isAuthenticated: hasToken,
      loading: hasToken,
      error: null,
      initialized: false,
    };
  } catch (error) {
    console.warn("Failed to read initial auth state:", error);
    return {
      user: null,
      isAuthenticated: false,
      loading: false,
      error: null,
      initialized: false,
    };
  }
};

const initialState = getInitialAuthState();

export const $authStore = createStore<AuthState>(initialState)
  .on(initializeAuthFx.pending, (state, pending) => ({
    ...state,
    loading: pending,
    error: pending ? null : state.error,
  }))
  .on(initializeAuthFx.doneData, (state, { isAuthenticated, user }) => ({
    ...state,
    isAuthenticated,
    user: user as User | null,
    loading: false,
    error: null,
    initialized: true,
  }))
  .on(initializeAuthFx.failData, (state, error) => ({
    ...state,
    isAuthenticated: false,
    user: null,
    loading: false,
    error: error.message || "Authentication initialization failed",
    initialized: true,
  }))
  .on(loginFx.pending, (state, pending) => ({
    ...state,
    loading: pending,
    error: pending ? null : state.error,
  }))
  .on(loginFx.doneData, (state, { user }) => ({
    ...state,
    user: user || null,
    isAuthenticated: Boolean(user),
    loading: false,
    error: null,
    initialized: true,
  }))
  .on(loginFx.failData, (state, error: any) => ({
    ...state,
    user: null,
    isAuthenticated: false,
    loading: false,
    error: error.message || "Login failed",
  }))
  .on(googleLoginFx.pending, (state, pending) => ({
    ...state,
    loading: pending,
    error: pending ? null : state.error,
  }))
  .on(googleLoginFx.doneData, (state, { user }) => ({
    ...state,
    user: user || null,
    isAuthenticated: Boolean(user),
    loading: false,
    error: null,
    initialized: true,
  }))
  .on(googleLoginFx.failData, (state, error) => ({
    ...state,
    user: null,
    isAuthenticated: false,
    loading: false,
    error: error.message || "Google login failed",
  }))
  .on(facebookLoginFx.pending, (state, pending) => ({
    ...state,
    loading: pending,
    error: pending ? null : state.error,
  }))
  .on(facebookLoginFx.doneData, (state, { user }) => ({
    ...state,
    user: user || null,
    isAuthenticated: Boolean(user),
    loading: false,
    error: null,
    initialized: true,
  }))
  .on(facebookLoginFx.failData, (state, error) => ({
    ...state,
    user: null,
    isAuthenticated: false,
    loading: false,
    error: error.message || "Facebook login failed",
  }))
  .on(linkedinLoginFx.pending, (state, pending) => ({
    ...state,
    loading: pending,
    error: pending ? null : state.error,
  }))
  .on(linkedinLoginFx.doneData, (state, { user }) => ({
    ...state,
    user: user || null,
    isAuthenticated: Boolean(user),
    loading: false,
    error: null,
    initialized: true,
  }))
  .on(linkedinLoginFx.failData, (state, error) => ({
    ...state,
    user: null,
    isAuthenticated: false,
    loading: false,
    error: error.message || "LinkedIn login failed",
  }))
  .on(logoutFx.done, () => ({
    user: null,
    isAuthenticated: false,
    loading: false,
    error: null,
    initialized: true,
  }));

// Samples
sample({
  clock: initializeAuth,
  target: initializeAuthFx,
});

sample({
  clock: loginRequested,
  target: loginFx,
});

sample({
  clock: googleLoginRequested,
  target: googleLoginFx,
});

sample({
  clock: facebookLoginRequested,
  target: facebookLoginFx,
});

sample({
  clock: linkedinCodeReceived,
  target: linkedinLoginFx,
});

sample({
  clock: logoutRequested,
  target: logoutFx,
});

// Fetch data after successful auth
sample({
  clock: initializeAuthFx.doneData,
  filter: ({ isAuthenticated }) => isAuthenticated,
  fn: () => ({ force: false }),
  target: fetchAllTermsRequested,
});

sample({
  clock: initializeAuthFx.doneData,
  filter: ({ isAuthenticated }) => isAuthenticated,
  target: fetchNotificationsRequested,
});

// After normal login
sample({
  clock: loginFx.doneData,
  fn: () => ({ force: false }),
  target: fetchAllTermsRequested,
});

sample({
  clock: loginFx.doneData,
  target: fetchNotificationsRequested,
});

// After Google login
sample({
  clock: googleLoginFx.doneData,
  fn: () => ({ force: false }),
  target: fetchAllTermsRequested,
});

sample({
  clock: googleLoginFx.doneData,
  target: fetchNotificationsRequested,
});

// After Facebook login
sample({
  clock: facebookLoginFx.doneData,
  fn: () => ({ force: false }),
  target: fetchAllTermsRequested,
});

sample({
  clock: facebookLoginFx.doneData,
  target: fetchNotificationsRequested,
});

// After LinkedIn login
sample({
  clock: linkedinLoginFx.doneData,
  fn: () => ({ force: false }),
  target: fetchAllTermsRequested,
});

sample({
  clock: linkedinLoginFx.doneData,
  target: fetchNotificationsRequested,
});

// ❌ REMOVE ALL THESE MERCURE SAMPLES
// sample({ clock: initializeAuthFx.doneData, ... target: initializeMercureFx });
// sample({ clock: loginFx.doneData, ... target: initializeMercureFx });
// sample({ clock: googleLoginFx.doneData, ... target: initializeMercureFx });
// sample({ clock: facebookLoginFx.doneData, ... target: initializeMercureFx });
// sample({ clock: linkedinLoginFx.doneData, ... target: initializeMercureFx });
